import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { usePathname, useRouter } from 'next/navigation';
import { useTransition } from 'react';

import { useRuntimeConfig } from '@/hooks/useRuntimeConfig';
import { createLoginRequest, createTokenRequest } from '@/lib/msalConfig';

import { useAuthStatus } from './useAuthStatus';

export const useAuth = () => {
    const { instance, inProgress } = useMsal();
    const { data: authStatus, invalidateAuthStatus } = useAuthStatus();
    const config = useRuntimeConfig();
    const [isInitializing, startInitializing] = useTransition();
    const router = useRouter();

    const homePath = '/';
    const loginPath = '/auth/login';
    const loginRedirectPath = '/auth/login-redirect';
    const logoutRedirectPath = '/auth/logout-redirect';
    const setPasswordPath = '/auth/set-password';
    const setPasswordSuccessPath = '/auth/set-password/success';

    // Routes that don't redirect to login page when user is not logged in
    const anonymousRoutes = [
        loginPath,
        setPasswordPath,
        setPasswordSuccessPath,
    ];

    const pathname = usePathname();

    const setToken = async (token: string) => {
        await fetch(`/api/auth/set-token`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            credentials: 'include', // Important: include cookies in requests
        });
    };

    const clearToken = async () => {
        await fetch(`/api/auth/clear-token`, {
            method: 'DELETE',
            credentials: 'include', // Important: include cookies in requests
        });
    };

    const initializeAuth = () => {
        startInitializing(async () => {
            await instance.initialize();

            if (
                pathname === loginRedirectPath ||
                pathname === logoutRedirectPath
            ) {
                // Handling is done directly on the redirect pages
                invalidateAuthStatus();
                return;
            }

            const account = instance.getActiveAccount();

            if (account) {
                const tokenResponse = await getAccountToken(account);

                // Send token to .NET API route to exchange for a cookie
                // Token is sent in Authorization header and validated by the API
                await setToken(tokenResponse.accessToken);

                if (pathname === loginPath) {
                    router.replace(homePath);
                }
            } else {
                // User is not logged in
                await clearToken();

                if (anonymousRoutes.includes(pathname)) {
                    return;
                }

                // Redirect to login page
                router.replace(loginPath);
            }

            invalidateAuthStatus();
        });
    };

    const getAccountToken = async (account: AccountInfo) => {
        const tokenRequest = createTokenRequest(config.azureAd.apiScope);

        return instance.acquireTokenSilent({
            ...tokenRequest,
            account,
        });
    };

    const login = async () => {
        const loginRequest = createLoginRequest(config.azureAd.apiScope);
        await instance.loginRedirect(loginRequest);
    };

    const handlePostLoginRedirect = async () => {
        const activeAccount = instance.getActiveAccount();

        if (activeAccount) {
            router.replace(homePath);
            return;
        }

        const redirectResult = await instance.handleRedirectPromise();

        if (redirectResult?.account) {
            const tokenResponse = await getAccountToken(redirectResult.account);
            await setToken(tokenResponse.accessToken);

            instance.setActiveAccount(redirectResult.account);
            router.replace(homePath);
        } else {
            // User is not logged in
            await clearToken();
            router.replace(loginPath);
        }

        invalidateAuthStatus();
    };

    const logout = async () => {
        await instance.clearCache();
        await clearToken();
        await instance.logoutRedirect();
    };

    const handlePostLogoutRedirect = async () => {
        if (instance.getActiveAccount()) {
            router.replace(homePath);
            return;
        }

        const redirectResult = await instance.handleRedirectPromise();
        const logoutSucceeded = redirectResult?.account === null;

        router.replace(logoutSucceeded ? homePath : loginPath);
        invalidateAuthStatus();
    };

    return {
        authStatus,
        authClientInstance: instance,
        isInitializing: inProgress !== InteractionStatus.None || isInitializing,
        login,
        handlePostLoginRedirect,
        logout,
        handlePostLogoutRedirect,
        initializeAuth,
    };
};
